import { type Attrs, type DOMOutputSpec, type Node, type NodeSpec } from "prosemirror-model";

import { CELL_SPEC_ATTRS, makeTextCellDefaultToDOMAttrs } from "@moment/prosemirror-utils";

// :: NodeSpec A code listing. Disallows marks or non-text inline
// nodes by default. Represented as a `<pre>` element with a
// `<code>` element inside of it.

export function parseCodeBlockMeta(meta: string) {
	const result: { lineNumbers: boolean; lineWrapping: boolean; rest: string } = {
		lineNumbers: false,
		lineWrapping: false,
		rest: "",
	};

	// Split by spaces to get individual key:value pairs
	const parts = meta.trim().split(/\s+/);
	const remainingParts: string[] = [];

	for (const part of parts) {
		if (part.includes(":")) {
			const [key, ...values] = part.split(":");
			const value = values.join(":");

			if (key === "lineNumbers") {
				result.lineNumbers = value === "true";
			} else if (key === "lineWrapping") {
				result.lineWrapping = value === "true";
			} else {
				// Unknown key:value pair, keep it in rest
				remainingParts.push(part);
			}
		} else {
			// Not a key:value pair, keep it in rest
			remainingParts.push(part);
		}
	}

	result.rest = remainingParts.join(" ");
	return result;
}

export function emitCodeBlockMeta(
	opts: { lineNumbers: boolean; lineWrapping: boolean },
	rest: string = ""
) {
	const { lineNumbers, lineWrapping } = opts;
	const parsedMeta = parseCodeBlockMeta(rest);
	parsedMeta.lineNumbers = lineNumbers;
	parsedMeta.lineWrapping = lineWrapping;

	let newMeta: string[] = [];
	if (parsedMeta.lineNumbers) {
		newMeta.push(`lineNumbers:${parsedMeta.lineNumbers}`);
	}
	if (parsedMeta.lineWrapping) {
		newMeta.push(`lineWrapping:${parsedMeta.lineWrapping}`);
	}
	newMeta.push(parsedMeta.rest);

	return newMeta.join(" ");
}

export const code_block: NodeSpec = {
	content: "text*",
	marks: "",
	group: "block",
	code: true,
	defining: true,
	isolating: true,
	attrs: {
		...CELL_SPEC_ATTRS,
		language: { default: null },
		meta: { default: emitCodeBlockMeta({ lineNumbers: true, lineWrapping: false }) },
	},
	parseDOM: [
		{
			tag: "pre",
			preserveWhitespace: "full",
			getAttrs(dom) {
				if (!(dom instanceof HTMLElement)) return {};
				const attrs: { language?: string | null; meta?: string } = {};
				const language = dom.getAttribute("data-language");
				const meta = dom.getAttribute("data-meta");
				if (language) {
					attrs.language = language;
				}
				if (meta) {
					attrs.meta = meta;
				}
				return attrs;
			},
		},
	],
	toDOM(node: Node) {
		const attrs: Attrs = makeTextCellDefaultToDOMAttrs(node, "pre relative");
		const preAttrs: Record<string, string> = {};
		if (node.attrs["language"]) {
			preAttrs["data-language"] = node.attrs["language"];
		}
		if (node.attrs["meta"]) {
			preAttrs["data-meta"] = node.attrs["meta"];
		}

		const preDOM: DOMOutputSpec = ["p", attrs, ["pre", preAttrs, ["code", 0]]];
		return preDOM;
	},
};
